import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Loader2, Search, X, Image as ImageIcon } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Mandala from "@/components/Mandala";
import { toast } from "sonner";
import AdminShell from "@/components/admin/AdminShell";

const PAGES = [
  { key: "home",       label: "Home page (holly portrait · book cover)",  path: "/" },
  { key: "about",      label: "About Holly (hero · horse · healer · retreat)", path: "/about" },
  { key: "journal",    label: "Journal — author headshot for essay footers", path: "/journal" },
  { key: "resources",  label: "Resources page (book cover)",              path: "/resources" },
  { key: "aerial-ytt", label: "Aerial Yoga Teacher Training", path: "/trainings/aerial-yoga" },
  { key: "solstice",   label: "Solstice in the Saddle Retreat", path: "/retreats/solstice-in-the-saddle" },
  { key: "winter-solstice", label: "Winter Solstice Retreat", path: "/retreats/winter-solstice-retreat" },
  { key: "destination-retreats", label: "Destination Retreats Landing", path: "/retreats" },
  { key: "become-trainer", label: "Become an Aerial Yoga Trainer", path: "/become-an-aerial-yoga-teacher-trainer" },
  { key: "restorative-ytt", label: "Restorative Online YTT", path: "/courses/restorative-aerial-yoga-teacher-training" },
];

// ───── Photo picker modal ─────
function PhotoPicker({ open, onClose, onSave, multi, max, initial = [] }) {
  const [allPhotos, setAllPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(initial);

  useEffect(() => { if (open) setSelected(initial); /* eslint-disable-next-line */ }, [open]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/photos");
        setAllPhotos(data);
      } catch (e) { toast.error("Failed to load photo library"); }
      finally { setLoading(false); }
    })();
  }, [open]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s ? allPhotos.filter((p) => p.filename.toLowerCase().includes(s)) : allPhotos;
  }, [allPhotos, search]);

  const togglePhoto = (id) => {
    setSelected((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (!multi) {
        // Single-select: replace + auto-save+close so the click sticks.
        // (Backdrop clicks used to lose unsaved selections.)
        setTimeout(() => { onSave([id]); onClose(); }, 0);
        return [id];
      }
      if (cur.length >= max) { toast.error(`Maximum ${max} photos for this slot`); return cur; }
      return [...cur, id];
    });
  };

  const move = (idx, dir) => {
    setSelected((cur) => {
      const a = [...cur];
      const j = idx + dir;
      if (j < 0 || j >= a.length) return cur;
      [a[idx], a[j]] = [a[j], a[idx]];
      return a;
    });
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-aviana-ink/70 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="photo-picker-modal">
      <div className="bg-aviana-paper rounded-2xl w-full max-w-6xl max-h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <header className="px-7 py-5 border-b border-aviana-border flex items-center justify-between bg-aviana-cream">
          <div>
            <p className="overline-plum mb-1">Photo library</p>
            <h3 className="font-display text-2xl text-aviana-ink">Pick {multi ? `up to ${max} photos` : "a photo"}</h3>
          </div>
          <button onClick={onClose} className="text-aviana-muted hover:text-aviana-ink" data-testid="picker-close"><X size={22} /></button>
        </header>

        {/* Selected order strip (when multi) */}
        {multi && selected.length > 0 && (
          <div className="px-7 py-4 border-b border-aviana-border bg-aviana-cream/50">
            <p className="overline text-aviana-gold text-[10px] mb-3">Selected order ({selected.length}/{max})</p>
            <div className="flex gap-3 flex-wrap" data-testid="picker-order">
              {selected.map((id, i) => {
                const p = allPhotos.find((x) => x.id === id);
                return (
                  <div key={id} className="relative group">
                    <img src={p?.url} alt="" className="w-20 h-20 rounded-lg object-cover ring-2 ring-aviana-gold" />
                    <span className="absolute -top-2 -left-2 bg-aviana-gold text-aviana-cream rounded-full w-6 h-6 text-[11px] font-bold flex items-center justify-center">{i+1}</span>
                    <div className="absolute inset-0 bg-aviana-ink/60 opacity-0 group-hover:opacity-100 rounded-lg flex items-center justify-center gap-1 transition-opacity">
                      {i > 0 && <button onClick={() => move(i, -1)} className="text-aviana-cream text-xs px-2 py-1 bg-aviana-plum rounded">←</button>}
                      {i < selected.length - 1 && <button onClick={() => move(i, 1)} className="text-aviana-cream text-xs px-2 py-1 bg-aviana-plum rounded">→</button>}
                      <button onClick={() => togglePhoto(id)} className="text-aviana-cream text-xs px-2 py-1 bg-red-700 rounded">✕</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="px-7 py-4 border-b border-aviana-border">
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-aviana-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by filename…"
              className="w-full pl-10 pr-4 py-2.5 rounded-full bg-aviana-cream border border-aviana-border text-[14px] outline-none focus:border-aviana-gold/60"
              data-testid="picker-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-auto p-7">
          {loading ? (
            <Loader2 className="animate-spin text-aviana-gold mx-auto" />
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3" data-testid="picker-grid">
              {filtered.map((p) => {
                const idx = selected.indexOf(p.id);
                const isSel = idx >= 0;
                return (
                  <button
                    key={p.id}
                    onClick={() => togglePhoto(p.id)}
                    className={`relative aspect-square rounded-lg overflow-hidden ring-2 transition-all ${isSel ? "ring-aviana-gold scale-95" : "ring-transparent hover:ring-aviana-gold/40"}`}
                    title={p.filename}
                    data-testid={`picker-photo-${p.id}`}
                  >
                    <img src={p.url} alt={p.filename} className="w-full h-full object-cover" loading="lazy" />
                    {isSel && (
                      <div className="absolute inset-0 bg-aviana-gold/30 flex items-center justify-center">
                        <span className="bg-aviana-gold text-aviana-cream rounded-full w-8 h-8 font-bold flex items-center justify-center">
                          {multi ? idx + 1 : <Check size={16} />}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-aviana-ink/80 to-transparent text-aviana-cream text-[10px] px-2 py-1 truncate">{p.filename}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <footer className="px-7 py-5 border-t border-aviana-border flex items-center justify-between bg-aviana-cream">
          <p className="text-[12px] text-aviana-muted">{filtered.length} of {allPhotos.length} photos</p>
          <div className="flex gap-3">
            <button onClick={onClose} className="text-[12px] tracking-[0.18em] uppercase text-aviana-muted hover:text-aviana-ink" data-testid="picker-cancel">Cancel</button>
            <button
              onClick={() => { onSave(selected); onClose(); }}
              disabled={!multi && selected.length === 0}
              className="btn-gold !py-2.5 !px-5 !text-[11px] disabled:opacity-50"
              data-testid="picker-save"
            >
              <Check size={14} /> Save selection
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ───── Slot card ─────
function SlotCard({ pageKey, slot, current, photos, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const save = async (newIds) => {
    setBusy(true);
    try {
      await api.put(`/admin/page-images/${pageKey}/${slot.key}`, { photo_ids: newIds });
      toast.success("Slot updated");
      onUpdated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-aviana-cream rounded-2xl border border-aviana-border p-6" data-testid={`slot-${slot.key}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="overline-plum text-[10px] mb-1">{slot.key}</p>
          <h3 className="font-display text-[17px] text-aviana-ink leading-tight">{slot.label}</h3>
          <p className="mt-1 text-[11px] text-aviana-muted">{slot.multi ? `Up to ${slot.max} photos` : "Single photo"}</p>
        </div>
        <button onClick={() => setPickerOpen(true)} disabled={busy} className="btn-secondary !py-2 !px-4 !text-[10px]" data-testid={`pick-${slot.key}`}>
          {current.length ? "Change" : "Pick photo"}
        </button>
      </div>
      <div className={`grid gap-2 ${slot.multi ? "grid-cols-3 sm:grid-cols-4" : "grid-cols-1"}`}>
        {current.length === 0 ? (
          <div className={`${slot.multi ? "col-span-full" : ""} aspect-video rounded-lg bg-aviana-paper border-2 border-dashed border-aviana-border flex items-center justify-center text-aviana-muted text-[12px] gap-2`}>
            <ImageIcon size={14} /> No photo set
          </div>
        ) : current.map((pid, i) => {
          const p = photos[pid];
          return (
            <div key={pid} className={`relative ${slot.multi ? "aspect-square" : "aspect-video"} rounded-lg overflow-hidden`}>
              {p ? <img src={p.url} alt={p.filename} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-aviana-border" />}
              {slot.multi && <span className="absolute top-1 left-1 bg-aviana-ink/70 text-aviana-cream rounded-full w-5 h-5 text-[10px] font-bold flex items-center justify-center">{i+1}</span>}
            </div>
          );
        })}
      </div>
      <PhotoPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSave={save} multi={!!slot.multi} max={slot.max || 1} initial={current} />
    </div>
  );
}

export default function AdminPageImages() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [pageKey, setPageKey] = useState(PAGES[0].key);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) { nav("/login"); return; }
    if (user && user.role !== "admin") { nav("/"); return; }
  }, [user, nav]);

  const load = async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get(`/admin/page-images/${pageKey}`);
      setData(r);
    } catch (e) { toast.error("Failed to load page slots"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [user, pageKey]);

  if (!user || user.role !== "admin") return null;

  const currentForSlot = (key) => (data?.assignments || []).filter((a) => a.slot_key === key).map((a) => a.photo_id);
  const page = PAGES.find((p) => p.key === pageKey);

  return (
    <AdminShell>
      <div className="max-w-6xl" data-testid="admin-page-images">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <p className="overline-plum mb-3">Curate visually</p>
            <h1 className="font-display text-4xl md:text-5xl text-aviana-ink">Page images</h1>
            <p className="mt-3 text-aviana-muted max-w-xl">Choose which photos appear on each landing page. Click a slot, browse your library, and pick the photos that bring the page to life.</p>
          </div>
          <Mandala size={72} variant="rose" className="text-aviana-gold opacity-60" />
        </div>

        {/* Page tabs */}
        <div className="flex gap-2 mb-7 flex-wrap" data-testid="page-tabs">
          {PAGES.map((p) => (
            <button
              key={p.key}
              onClick={() => setPageKey(p.key)}
              className={`px-5 py-2.5 rounded-full text-[12px] tracking-[0.14em] uppercase border transition-colors ${pageKey === p.key ? "bg-aviana-ink text-aviana-cream border-aviana-ink" : "border-aviana-border text-aviana-muted hover:text-aviana-ink"}`}
              data-testid={`page-tab-${p.key}`}
            >
              {p.label}
            </button>
          ))}
          {page && (
            <a href={page.path} target="_blank" rel="noreferrer" className="ml-auto text-[12px] tracking-[0.18em] uppercase text-aviana-plum hover:text-aviana-gold flex items-center gap-2" data-testid="preview-page">
              Preview page →
            </a>
          )}
        </div>

        {loading || !data ? (
          <Loader2 className="animate-spin text-aviana-gold mx-auto py-20" />
        ) : (
          <div className="grid md:grid-cols-2 gap-5" data-testid="slots-grid">
            {data.slots.map((slot) => (
              <SlotCard
                key={slot.key}
                pageKey={pageKey}
                slot={slot}
                current={currentForSlot(slot.key)}
                photos={data.photos}
                onUpdated={load}
              />
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
